import type { StorageValue } from 'zustand/middleware';
import { getDefaultLocale } from 'intl/helpers';
import { isSupportedLocale } from 'shared/utils/locale/isSupportedLocale';
import type { PersistedUiState, UiState } from './types';

export const UI_PERSIST_VERSION = 6;

export function createInitialUiState(): UiState {
  return {
    currentLocale: getDefaultLocale(),
    isFormEditing: {},
    mismatchedVersion: false,
  };
}

export function setFormEditing(
  state: UiState,
  form: string,
  isFormEditing: boolean
): UiState {
  if (state.isFormEditing[form] === isFormEditing) {
    return state;
  }

  return {
    ...state,
    isFormEditing: { ...state.isFormEditing, [form]: isFormEditing },
  };
}

export function isAnyFormEditing(state: UiState): boolean {
  return Object.values(state.isFormEditing).some(Boolean);
}

function parseLocale(value: unknown): PersistedUiState | null {
  return typeof value === 'string' && isSupportedLocale(value)
    ? { currentLocale: value }
    : null;
}

// Accepts both the current format and the legacy redux-persist `persist:ui` entry.
export function parsePersistedUiState(
  raw: unknown
): StorageValue<PersistedUiState> | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  if ('state' in raw) {
    const state = parseLocale(
      (raw as { state?: { currentLocale?: unknown } }).state?.currentLocale
    );
    return state ? { state, version: UI_PERSIST_VERSION } : null;
  }

  const legacyLocale = (raw as { currentLocale?: unknown }).currentLocale;
  if (typeof legacyLocale !== 'string') {
    return null;
  }

  try {
    const state = parseLocale(JSON.parse(legacyLocale));
    return state ? { state, version: UI_PERSIST_VERSION } : null;
  } catch {
    return null;
  }
}
